/**
 * Debounce utility for performance optimization
 */

/**
 * Creates a debounced version of a function that delays execution
 * until after `wait` milliseconds have passed since the last call.
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): T & { cancel: () => void; flush: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;

  const debounced = (...args: Parameters<T>) => {
    lastArgs = args;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      if (lastArgs) {
        func(...lastArgs);
      }
      timeoutId = null;
      lastArgs = null;
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
      lastArgs = null;
    }
  };

  debounced.flush = () => {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      func(...lastArgs);
      timeoutId = null;
      lastArgs = null;
    }
  };

  return debounced as T & { cancel: () => void; flush: () => void };
}

/**
 * Creates debounced localStorage write functions
 * Batches multiple writes into a single operation after a delay
 */
export function createDebouncedStorage<T>(
  key: string,
  wait: number = 2000
): {
  write: (data: T) => void;
  flush: () => void;
  destroy: () => void;
} {
  let pendingData: T | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (pendingData !== null) {
      try {
        localStorage.setItem(key, JSON.stringify(pendingData));
      } catch (error) {
        console.error(`[Storage] Failed to write ${key}:`, error);
      }
      pendingData = null;
    }
  };

  const write = (data: T) => {
    pendingData = data;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(flush, wait);
  };

  // Flush on page unload to prevent data loss
  const unloadHandler = () => flush();
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', unloadHandler);
  }

  const destroy = () => {
    flush();
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', unloadHandler);
    }
  };

  return { write, flush, destroy };
}

/**
 * Creates debounced storage that writes to both localStorage AND DynamoDB
 * localStorage = fast cache, DynamoDB = shared persistence
 */
export function createDebouncedCloudStorage<T>(
  localKey: string,
  cloudKey: string,
  wait: number = 3000
): {
  write: (data: T) => void;
  flush: () => void;
  destroy: () => void;
  loadFromCloud: () => Promise<T | null>;
} {
  let pendingData: T | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  let lastWriteTs = 0;

  const saveToCloud = async (data: T) => {
    const ts = Date.now();
    lastWriteTs = ts;
    try {
      const response = await fetch('/api/appdata/' + encodeURIComponent(cloudKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ data, _updatedAt: ts }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch (err) {
      console.warn(`[CloudStorage] Failed to save ${cloudKey} to DynamoDB:`, err);
    }
  };

  const flush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (pendingData !== null) {
      const data = pendingData;
      pendingData = null;
      try {
        localStorage.setItem(localKey, JSON.stringify(data));
      } catch (error) {
        console.error(`[CloudStorage] Failed to write ${localKey}:`, error);
      }
      saveToCloud(data);
    }
  };

  const write = (data: T) => {
    pendingData = data;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(flush, wait);
  };

  const loadFromCloud = async (): Promise<T | null> => {
    try {
      const response = await fetch('/api/appdata/' + encodeURIComponent(cloudKey), {
        credentials: 'include',
      });
      if (!response.ok) return null;
      const result = await response.json();
      if (result.data !== null && result.data !== undefined) {
        // Conflict resolution: cloud wins only if it's newer than our last write
        const cloudTs = (result.data as Record<string, unknown>)?._updatedAt as number || 0;
        if (lastWriteTs > 0 && cloudTs < lastWriteTs) {
          // Local is newer — don't overwrite
          return null;
        }
        // Strip internal timestamp before returning
        const cleaned = result.data;
        if (typeof cleaned === 'object' && cleaned !== null && '_updatedAt' in (cleaned as Record<string, unknown>)) {
          delete (cleaned as Record<string, unknown>)._updatedAt;
        }
        // Update localStorage cache
        try {
          localStorage.setItem(localKey, JSON.stringify(cleaned));
        } catch {}
        return cleaned as T;
      }
    } catch (err) {
      console.warn(`[CloudStorage] Failed to load ${cloudKey} from DynamoDB:`, err);
    }
    return null;
  };

  const unloadHandler = () => {
    if (pendingData !== null) {
      try {
        localStorage.setItem(localKey, JSON.stringify(pendingData));
      } catch {}
      // Use sendBeacon for reliable save on page close
      try {
        navigator.sendBeacon(
          '/api/appdata/' + encodeURIComponent(cloudKey),
          new Blob([JSON.stringify({ data: pendingData })], { type: 'application/json' })
        );
      } catch {}
      pendingData = null;
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', unloadHandler);
  }

  const destroy = () => {
    flush();
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', unloadHandler);
    }
  };

  return { write, flush, destroy, loadFromCloud };
}
